import { DfyPaymentMethod } from "@repo/api/dfy";
import { ReactNode } from "react";
import { formatDfyCreditsToPrice } from "../dfy-order-form-content";
import { SELECTED_CARD_STYLE } from "../dfy-order-form-styles";
import { IconCheck, IconCreditCard } from "@tabler/icons-react";
import { CreditCoverage } from "../dfy-order-form-hooks";

function getCreditOptionContent({
  creditCoverage,
  hasBillingEditPermission,
}: {
  creditCoverage: CreditCoverage;
  hasBillingEditPermission: boolean;
}) {
  if (creditCoverage.type === "PARTIAL" && !hasBillingEditPermission) {
    return {
      title: "Pay with credits and card",
      description: `Your credits cover ${formatDfyCreditsToPrice(creditCoverage.coveredCredits)}. You don't have permission to pay the remaining ${formatDfyCreditsToPrice(creditCoverage.remainingCredits)} with Stripe.`,
    };
  } else if (creditCoverage.type === "PARTIAL") {
    return {
      title: "Pay with credits and card",
      description: `Your credits cover ${formatDfyCreditsToPrice(creditCoverage.coveredCredits)}. The remaining ${formatDfyCreditsToPrice(creditCoverage.remainingCredits)} is paid with card.`,
    };
  } else if (creditCoverage.type === "FULL") {
    return {
      title: "Pay with credits",
      description: `${creditCoverage.coveredCredits.toLocaleString()} credits`,
    };
  } else {
    return {
      title: "Pay with credits",
      description: "You don't have any credits to use",
    };
  }
}

function getStripeOptionDescription({
  hasBillingEditPermission,
  totalCredits,
}: {
  hasBillingEditPermission: boolean;
  totalCredits: number;
}) {
  if (!hasBillingEditPermission) {
    return "You don't have permission to pay with card";
  }

  return formatDfyCreditsToPrice(totalCredits);
}

function CardLogos() {
  return (
    <span className="flex items-center gap-1.5">
      <img src="/visa-logo.png" alt="Visa" className="h-3 w-auto" />
      <img src="/mastercard-logo.png" alt="Mastercard" className="h-3 w-auto" />
      <img src="/amex-logo.png" alt="American Express" className="h-3 w-auto" />
    </span>
  );
}

function PaymentOptionCard({
  icon,
  title,
  description,
  accessory,
  isSelected,
  disabled = false,
  onSelect,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  accessory?: ReactNode;
  isSelected: boolean;
  disabled?: boolean;
  onSelect: () => void;
}) {
  const dimmed = disabled ? "opacity-50" : "";

  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onSelect}
      className={`rounded-xl border text-left ${
        disabled ? "cursor-not-allowed" : "cursor-pointer"
      } ${isSelected ? "border-transparent" : ""}`}
      style={isSelected ? SELECTED_CARD_STYLE : undefined}
    >
      <div className="flex items-center gap-4 p-4">
        <span
          className={`flex size-10 shrink-0 items-center justify-center ${dimmed}`}
        >
          {icon}
        </span>
        <div className={`flex min-w-0 flex-1 flex-col ${dimmed}`}>
          <span className="font-medium">{title}</span>
          <span className="text-sm text-text-secondary">{description}</span>
        </div>
        {accessory && (
          <span className={`mr-2 shrink-0 ${dimmed}`}>{accessory}</span>
        )}
        <span
          className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full border ${
            isSelected
              ? "border-transparent bg-linear-to-br from-primary-400 to-secondary-400 text-white"
              : dimmed
          }`}
        >
          {isSelected && <IconCheck className="h-3.5 w-3.5" />}
        </span>
      </div>
    </button>
  );
}

export default function PaymentMethodPicker({
  paymentMethod,
  handleChangePaymentMethod,
  creditCoverage,
  isOrganisation,
  allowedPaymentMethods,
  hasBillingEditPermission,
  totalCredits,
}: {
  paymentMethod: DfyPaymentMethod;
  handleChangePaymentMethod: (paymentMethod: DfyPaymentMethod) => void;
  creditCoverage: CreditCoverage;
  isOrganisation: boolean;
  allowedPaymentMethods: DfyPaymentMethod[];
  hasBillingEditPermission: boolean;
  totalCredits: number;
}) {
  if (isOrganisation) {
    return null;
  }

  const creditOptionContent = getCreditOptionContent({
    creditCoverage,
    hasBillingEditPermission,
  });

  return (
    <div className="flex flex-col gap-3">
      <p className="font-medium">Payment</p>
      <div className="flex flex-col gap-2">
        <PaymentOptionCard
          icon={<img src="/CreditIcon.svg" alt="" className="h-6 w-6" />}
          title={creditOptionContent.title}
          description={creditOptionContent.description}
          isSelected={
            allowedPaymentMethods.includes("CREDITS") &&
            paymentMethod === "CREDITS"
          }
          disabled={!allowedPaymentMethods.includes("CREDITS")}
          onSelect={() => handleChangePaymentMethod("CREDITS")}
        />
        <PaymentOptionCard
          icon={
            <IconCreditCard className="h-6 w-6" color="url(#icon-gradient)" />
          }
          title="Pay with card"
          description={getStripeOptionDescription({
            hasBillingEditPermission,
            totalCredits,
          })}
          accessory={<CardLogos />}
          isSelected={
            allowedPaymentMethods.includes("STRIPE") &&
            paymentMethod === "STRIPE"
          }
          disabled={!allowedPaymentMethods.includes("STRIPE")}
          onSelect={() => handleChangePaymentMethod("STRIPE")}
        />
      </div>
    </div>
  );
}
